import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  clearSession,
  completeNewPassword as libCompleteNewPassword,
  confirmForgotPassword as libConfirmForgotPassword,
  forgotPassword as libForgotPassword,
  getClaims,
  signIn as libSignIn,
  type Claims,
  type SignInResult,
} from "./auth";

type AuthContextValue = {
  user: Claims | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  completeNewPassword: (newPassword: string) => Promise<SignInResult>;
  /** Emails a reset code. Callers should show a neutral message whatever happens. */
  forgotPassword: (email: string) => Promise<void>;
  /** Sets a new password with the emailed code. */
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Claims | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const claims = await getClaims();
    setUser(claims);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getClaims()
      .then((claims) => {
        if (!cancelled) setUser(claims);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await libSignIn(email, password);
      if (result.status === "success") await refresh();
      return result;
    },
    [refresh],
  );

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      const result = await libCompleteNewPassword(newPassword);
      if (result.status === "success") await refresh();
      return result;
    },
    [refresh],
  );

  const forgotPassword = useCallback((email: string) => libForgotPassword(email), []);

  const confirmForgotPassword = useCallback(
    (email: string, code: string, newPassword: string) =>
      libConfirmForgotPassword(email, code, newPassword),
    [],
  );

  const signOut = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAdmin: !!user?.roles.includes("admin"),
      loading,
      signIn,
      completeNewPassword,
      forgotPassword,
      confirmForgotPassword,
      signOut,
      refresh,
    }),
    [user, loading, signIn, completeNewPassword, forgotPassword, confirmForgotPassword, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
